import React, { FC, useEffect, useRef, useState, KeyboardEvent } from 'react';

import { useTheme } from '@mui/material';
import { styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Configure, InstantSearch } from 'react-instantsearch';
import { useLocation } from 'react-router-dom';

import CloseIcon from '~/assets/icons/CloseIcon';
import SearchIcon from '~/assets/icons/SearchIcon';
import QuerySuggestions from '~/components/Search/QuerySuggestions';
import useSearchClient from '~/hooks/useSearchClient';
import useIconAnimation from '~/libs/hooks/animations/useIconAnimation';
import { CssUtilityType } from '~/styled';
import { SMALL_MARGIN_PX } from '~/theme';

interface SearchProps {
  currentRefinement?: string;
  wrapperClass?: string;
  refine: Function;
  handleClear?: Function;
  withClear?: boolean;
  withAutocomplete?: boolean;
  searchPlaceholder?: string;
  wrapperStyles?: CssUtilityType;
}

const Search: FC<SearchProps> = ({
  currentRefinement = '',
  refine,
  handleClear = undefined,
  withClear = false,
  withAutocomplete = false,
  searchPlaceholder,
  wrapperStyles,
}) => {
  const location = useLocation();
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { searchClient, indexName } = useSearchClient('query_suggestions');

  const theme = useTheme();

  const { scope, play } = useIconAnimation('SearchIcon');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get('s');
    if (s) {
      refine(s);
    }
  }, [location, refine]);

  const onChange = (e) => {
    const value = e?.target?.value;
    setValue(value || '');
  };

  const onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef?.current?.blur();
    }
  };

  const resetSearch = () => {
    setValue('');
    handleClear && handleClear();
  };

  const search = (newValue?: string) => {
    refine(newValue || value);
  };

  useEffect(() => {
    setValue(currentRefinement);
  }, [currentRefinement, location]);

  return (
    <InstantSearch searchClient={searchClient} indexName={indexName}>
      <Configure hitsPerPage={10} distinct={3} clickAnalytics={true} />
      <QuerySuggestionsWrapper optionalStyles={wrapperStyles}>
        {withClear && value && (
          <CloseIconWrapper
            data-testid="clearSearchBox"
            aria-label="clear"
            onClick={resetSearch}
            size="large"
          >
            <CloseIcon color={theme.palette.common.header.color} />
          </CloseIconWrapper>
        )}
        <QuerySuggestions
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          inputRef={inputRef}
          withAutocomplete={withAutocomplete}
          search={search}
          placeholder={searchPlaceholder}
        />
        <StyledIconBtn
          data-testid="submitSearch"
          type="submit"
          aria-label="search"
          onClick={() => search()}
          size="large"
          onMouseEnter={play}
        >
          <StyledSearchIcon ref={scope} />
        </StyledIconBtn>
      </QuerySuggestionsWrapper>
    </InstantSearch>
  );
};

export default Search;

const QuerySuggestionsWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles?: CssUtilityType }>`
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  height: 60px;
  width: 100%;
  max-width: 100%;
  margin-bottom: ${SMALL_MARGIN_PX};
  padding-left: 30px;
  padding-right: 9px;
  border-radius: 999em;
  //background: ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  background: ${({ theme }) => theme.palette.common.card.background.light};
  color: ${({ theme }) => theme.palette.common.header.color};

  &:hover {
    box-shadow: ${({ theme }) => theme.palette.common.card.shadowValue};
    color: ${({ theme }) => theme.palette.common.star};
  }

  &:hover .MuiAutocomplete-input::placeholder {
    color: ${({ theme }) => theme.palette.common.star};
    opacity: 1;
  }

  & .MuiAutocomplete-root {
    width: 100%;
    display: flex;
    align-items: center;
  }

  ${({ theme }) => theme.breakpoints.up('sm')} {
    width: 600px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${SMALL_MARGIN_PX};
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const CloseIconWrapper = styled(IconButton)`
  position: absolute;
  color: unset;
  z-index: 1;
  left: 9px;
  & svg {
    width: 17.8px;
    height: 17.8px;
  }
`;
const StyledIconBtn = styled(IconButton)`
  flex: 0;
  transition: 0.3s color;
  color: inherit;
`;
const StyledSearchIcon = styled(SearchIcon)`
  color: inherit;
  widht: 18px;
  height: 18px;
`;
